import { HallOfFameSearchFormData } from "components/hall-of-fame/HallOfFameSearchForm";
import { HallOfFameSearchParams } from "pages/hall-of-fame/HallOfFameSearchPage";
import { LazyExoticComponent, lazy } from "react";
import { Constants } from "utils/Constants";
import { toSafeUrlName } from "utils/StringUtils";

type AuthType = "Public" | "Teachers" | "AuthRequired" | "AuthBlocked" | "NotStudents";

const AccessibilityPage = lazy(() => import("pages/AccessibilityPage"));
const AccountExistsPage = lazy(() => import("pages/account/AccountExistsPage"));
const ClassesAddStudentsPage = lazy(() => import("pages/classes/ClassesAddStudentsPage"));
const ClassesClassGamesPage = lazy(() => import("pages/classes/ClassesClassGamesPage"));
const ClassesClassManagePage = lazy(() => import("pages/classes/ClassesClassManagePage"));
const ClassesClassPage = lazy(() => import("pages/classes/ClassesClassPage"));
const ClassesClassShowcasePage = lazy(() => import("pages/classes/ClassesClassShowcasePage"));
const ClassesClassStudentPage = lazy(() => import("pages/classes/ClassesClassStudentPage"));
const ClassesClassStudentRepliesPage = lazy(() => import("pages/classes/ClassesClassStudentRepliesPage"));
const ClassesCreatePage = lazy(() => import("pages/classes/ClassesCreatePage"));
const ClassesJoinPage = lazy(() => import("pages/classes/ClassesJoinPage"));
const ClassesPage = lazy(() => import("pages/classes/ClassesPage"));
const CompetitionTermsPage = lazy(() => import("pages/CompetitionTermsPage"));
const ContactPage = lazy(() => import("pages/footer/ContactPage"));
const DiscoverCategoryPage = lazy(() => import("pages/discover/DiscoverCategoryPage"));
const DiscoverFindPromptPage = lazy(() => import("pages/discover/DiscoverFindPromptPage"));
const DiscoverFindSetPage = lazy(() => import("pages/discover/DiscoverFindSetPage"));
const DiscoverPage = lazy(() => import("pages/discover/DiscoverPage"));
const DiscoverPromptPage = lazy(() => import("pages/discover/DiscoverPromptPage"));
const DiscoverPublisherPage = lazy(() => import("pages/discover/DiscoverPublisherPage"));
const DiscoverSearchPage = lazy(() => import("pages/discover/DiscoverSearchPage"));
const DiscoverSetPage = lazy(() => import("pages/discover/DiscoverSetPage"));
const EduTechPage = lazy(() => import("pages/EduTechPage"));
const EventsPage = lazy(() => import("pages/footer/EventsPage"));
const FaqsPage = lazy(() => import("pages/footer/FaqsPage"));
const FriendsPage = lazy(() => import("pages/FriendsPage"));
const HallOfFameGamePage = lazy(() => import("pages/hall-of-fame/HallOfFameGamePage"));
const HallOfFamePage = lazy(() => import("pages/hall-of-fame/HallOfFamePage"));
const HallOfFameSearchPage = lazy(() => import("pages/hall-of-fame/HallOfFameSearchPage"));
const HomePage = lazy(() => import("pages/HomePage"));
const LoginPage = lazy(() => import("pages/account/LoginPage"));
const NswCompletePage = lazy(() => import("pages/account/NswCompletePage"));
const NewsletterPage = lazy(() => import("pages/footer/NewsletterPage"));
const MyGamesActiveHostMultiPage = lazy(() => import("pages/my-games/MyGamesActiveHostMultiPage"));
const MyGamesActiveHostPage = lazy(() => import("pages/my-games/MyGamesActiveHostPage"));
const MyGamesActivePage = lazy(() => import("pages/my-games/MyGamesActivePage"));
const MyGamesActivePlayerPage = lazy(() => import("pages/my-games/MyGamesActivePlayerPage"));
const MyGamesEnterGamePage = lazy(() => import("pages/my-games/MyGamesEnterGamePage"));
const MyGamesPolishGamePage = lazy(() => import("pages/my-games/MyGamesPolishGamePage"));
const MyGamesGamePage = lazy(() => import("pages/my-games/MyGamesGamePage"));
const MyGamesHomeworkPage = lazy(() => import("pages/my-games/MyGamesHomeworkPage"));
const MyGamesHostGamePage = lazy(() => import("pages/my-games/MyGamesHostGamePage"));
const MyGamesHostGameSettingsPage = lazy(() => import("pages/my-games/MyGamesHostGameSettingsPage"));
const MyGamesJoinGamePage = lazy(() => import("pages/my-games/MyGamesJoinGamePage"));
const MyGamesMyPromptsPage = lazy(() => import("pages/my-games/MyGamesMyPromptsPage"));
const MyGamesNewGamePage = lazy(() => import("pages/my-games/MyGamesNewGamePage"));
const MyGamesPage = lazy(() => import("pages/my-games/MyGamesPage"));
const MyGamesPastHostGamePage = lazy(() => import("pages/my-games/MyGamesPastHostGamePage"));
const MyGamesPastHostPage = lazy(() => import("pages/my-games/MyGamesPastHostPage"));
const MyGamesPastPage = lazy(() => import("pages/my-games/MyGamesPastPage"));
const MyGamesPastPlayerGamePage = lazy(() => import("pages/my-games/MyGamesPastPlayerGamePage"));
const MyGamesPastPlayerPage = lazy(() => import("pages/my-games/MyGamesPastPlayerPage"));
const MyGamesProjectorPage = lazy(() => import("pages/my-games/MyGamesProjectorPage"));
const MyGamesSpectatePage = lazy(() => import("pages/my-games/MyGamesSpectatePage"));
const MyGamesTemplateAssignAsHomeworkPage = lazy(() => import("pages/my-games/MyGamesTemplateAssignAsHomeworkPage"));
const MyGamesTemplateAssignToClassPage = lazy(() => import("pages/my-games/MyGamesTemplateAssignToClassPage"));
const MyGamesTemplateEditPage = lazy(() => import("pages/my-games/MyGamesTemplateEditPage"));
const MyGamesTemplatePage = lazy(() => import("pages/my-games/MyGamesTemplatePage"));
const PricingPage = lazy(() => import("pages/PricingPage"));
const PrivacyPage = lazy(() => import("pages/footer/PrivacyPage"));
const ProfileChangePasswordPage = lazy(() => import("pages/profile/ProfileChangePasswordPage"));
const ProfileDefaultsPage = lazy(() => import("pages/profile/ProfileDefaultsPage"));
const ProfileDetailsPage = lazy(() => import("pages/profile/ProfileDetailsPage"));
const ProfileJoinSchoolPage = lazy(() => import("pages/profile/ProfileJoinSchoolPage"));
const ProfileLeaveSchoolPage = lazy(() => import("pages/profile/ProfileLeaveSchoolPage"));
const ProfileMyAvatarsPage = lazy(() => import("pages/profile/ProfileMyAvatarsPage"));
const ProfileMySchoolPage = lazy(() => import("pages/profile/ProfileMySchoolPage"));
const ProfilePublishCreateSetPage = lazy(() => import("pages/profile/publish/ProfilePublishCreateSetPage"));
const ProfilePublishPage = lazy(() => import("pages/profile/publish/ProfilePublishPage"));
const ProfilePublishRegisterPage = lazy(() => import("pages/profile/publish/ProfilePublishRegisterPage"));
const ProfilePublishSetCreatePromptPage = lazy(() => import("pages/profile/publish/ProfilePublishSetCreatePromptPage"));
const ProfilePublishSetImportPage = lazy(() => import("pages/profile/publish/ProfilePublishSetImportPage"));
const ProfilePublishSetPage = lazy(() => import("pages/profile/publish/ProfilePublishSetPage"));
const ProfilePublishSetUpdatePromptPage = lazy(() => import("pages/profile/publish/ProfilePublishSetUpdatePromptPage"));
const ProfilePublishUpdateProfilePage = lazy(() => import("pages/profile/publish/ProfilePublishUpdateProfilePage"));
const ProfilePublishUpdateSetPage = lazy(() => import("pages/profile/publish/ProfilePublishUpdateSetPage"));
const ProfileReferAFriendPage = lazy(() => import("pages/profile/ProfileReferAFriendPage"));
const ProfileSubscriptionPage = lazy(() => import("pages/profile/ProfileSubscriptionPage"));
const ProfileWatchListPage = lazy(() => import("pages/profile/ProfileWatchListPage"));
const RandomPage = lazy(() => import("pages/RandomPage"));
const RecoverPasswordPage = lazy(() => import("pages/account/RecoverPasswordPage"));
const ReferAFriendPage = lazy(() => import("pages/footer/ReferAFriendPage"));
const RegisterPage = lazy(() => import("pages/account/RegisterPage"));
const RequestInvoicePage = lazy(() => import("pages/footer/RequestInvoicePage"));
const ResourcesPage = lazy(() => import("pages/footer/ResourcesPage"));
const SignupCompleteStudentPage = lazy(() => import("pages/account/SignupCompleteStudentPage"));
const SignupCompleteTeacherPage = lazy(() => import("pages/account/SignupCompleteTeacherPage"));
const StudentWritingResourcesPage = lazy(() => import("pages/StudentWritingResourcesPage"));
const SubscriptionsPurchaseSuccessPage = lazy(() => import("pages/subscriptions/SubscriptionsPurchaseSuccessPage"));
const TeamPage = lazy(() => import("pages/footer/TeamPage"));
const TermsPage = lazy(() => import("pages/footer/TermsPage"));
const VerifyEmailChangePage = lazy(() => import("pages/account/VerifyEmailChangePage"));
const VideosPage = lazy(() => import("pages/footer/VideosPage"));
const YearbookPage = lazy(() => import("pages/landing/YearbookPage"));

export const RouteList = {
    Accessibility: "/accessibility",
    AccountExists: "/account-exists",
    Classes: "/classes",
    ClassesCreate: "/classes/create",
    ClassesJoin: "/classes/join",
    CompetitionTerms: "/comp-terms",
    Contact: "/contact-us",
    Discover: "/discover",
    DiscoverSearch: "/discover/search",
    EduTech: "/edutech",
    Events: "/events",
    Faqs: "/faqs",
    Friends: "/friends",
    HallOfFame: "/hall-of-fame",
    HallOfFameSearch: "/hall-of-fame/search",
    Home: "/",
    LandingShakespeare: "/shakespeare",
    LandingWritingStamina: "/writing-stamina",
    Login: "/login",
    NswComplete: "/auth/nsw-complete",
    LoginStudent: "/login?t=student",
    LoginTeacher: "/login?t=teacher",
    Newsletter: "/newsletter",
    MyGames: "/my-games",
    MyGamesActive: "/my-games/active",
    MyGamesActiveHost: "/my-games/active/host",
    MyGamesActiveHostMulti: "/my-games/active/host/multi",
    MyGamesActivePlayer: "/my-games/active/player",
    MyGamesHomework: "/my-games/homework",
    MyGamesJoinGame: "/my-games/join-game",
    MyGamesMyPrompts: "/my-games/my-prompts",
    MyGamesNewGame: "/my-games/new-game",
    MyGamesPast: "/my-games/past",
    MyGamesPastHost: "/my-games/past/host",
    MyGamesPastPlayer: "/my-games/past/player",
    Pricing: "/pricing",
    Privacy: "/privacy",
    Profile: "/profile",
    ProfileChangePassword: "/profile/change-password",
    ProfileDefaults: "/profile/defaults",
    ProfileDetails: "/profile",
    ProfileJoinSchool: "/profile/school/join",
    ProfileLeaveSchool: "/profile/school/leave",
    ProfileMyAvatars: "/profile/avatars",
    ProfilePublish: "/profile/publish",
    ProfilePublishCreateSet: "/profile/publish/create-set",
    ProfilePublishRegister: "/profile/publish/register",
    ProfilePublishUpdateProfile: "/profile/publish/update",
    ProfileReferralCode: "/profile/referrals",
    ProfileSchool: "/profile/school",
    ProfileSubscription: "/profile/subscription",
    ProfileSubscriptionPurchaseSuccess: "/profile/subscription/purchase-success",
    ProfileWatchList: "/profile/watch-list",
    Random: "/random-page",
    ReferAFriend: "/refer-a-friend",
    RequestInvoice: "/request-invoice",
    Resources: "/resources",
    Reviews: "/reviews",
    Signup: "/signup",
    SignupCompleteStudent: "/signup-complete-student",
    SignupCompleteTeacher: "/signup-complete-teacher",
    SignupCompleteTeacherReferrer: "/signup-complete-teacher?r=1",
    SignupTeacher: "/signup?t=teacher",
    StudentWritingResources: "/student-writing-resources",
    Team: "/team",
    Terms: "/terms",
    VerifyEmailChange: "/verify-email-change/:hash",
    Videos: "/videos",
    Writelike: Constants.webUrl,
    Yearbook: "/discover/yearbooks",
};

interface IRouteTableItem {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Component: LazyExoticComponent<any>;
    path: string;
    authType: AuthType;
}

export const RouteTable: Array<IRouteTableItem> = [
    {
        path: RouteList.MyGames,
        Component: MyGamesPage,
        authType: "Public",
    },
    {
        path: RouteList.MyGamesJoinGame,
        Component: MyGamesJoinGamePage,
        authType: "Public",
    },
    {
        path: RouteList.MyGamesNewGame,
        Component: MyGamesNewGamePage,
        authType: "Public",
    },
    {
        path: RouteList.MyGamesActive,
        Component: MyGamesActivePage,
        authType: "Public",
    },
    {
        path: RouteList.MyGamesActivePlayer,
        Component: MyGamesActivePlayerPage,
        authType: "Public",
    },
    {
        path: RouteList.MyGamesActiveHost,
        Component: MyGamesActiveHostPage,
        authType: "Teachers",
    },
    {
        path: RouteList.MyGamesActiveHostMulti,
        Component: MyGamesActiveHostMultiPage,
        authType: "Teachers",
    },
    {
        path: RouteList.MyGamesMyPrompts,
        Component: MyGamesMyPromptsPage,
        authType: "Teachers",
    },
    {
        path: RouteList.MyGamesPast,
        Component: MyGamesPastPage,
        authType: "Public",
    },
    {
        path: RouteList.MyGamesPastPlayer,
        Component: MyGamesPastPlayerPage,
        authType: "Public",
    },
    {
        path: RouteList.MyGamesHomework,
        Component: MyGamesHomeworkPage,
        authType: "AuthRequired",
    },
    {
        path: RouteList.MyGamesPastHost,
        Component: MyGamesPastHostPage,
        authType: "Teachers",
    },
    {
        path: "/recover-password/:hash",
        Component: RecoverPasswordPage,
        authType: "Public",
    },
    {
        path: RouteList.NswComplete,
        Component: NswCompletePage,
        authType: "Public",
    },
    {
        path: "/verify-email-change/:hash",
        Component: VerifyEmailChangePage,
        authType: "Public",
    },
    {
        path: "/my-games/game/:code([A-Za-z0-9]{6})",
        Component: MyGamesGamePage,
        authType: "Public",
    },
    {
        path: "/my-games/my-prompts/:id([0-9]+)/edit/",
        Component: MyGamesTemplateEditPage,
        authType: "Teachers",
    },
    {
        path: "/my-games/host/:code([A-Za-z0-9]{6})",
        Component: MyGamesHostGamePage,
        authType: "Teachers",
    },
    {
        path: "/my-games/past/host/:code([A-Za-z0-9]{6})",
        Component: MyGamesPastHostGamePage,
        authType: "Teachers",
    },
    {
        path: "/my-games/my-prompts/:id([0-9]+)/assign-to-class",
        Component: MyGamesTemplateAssignToClassPage,
        authType: "Teachers",
    },
    {
        path: "/my-games/my-prompts/:id([0-9]+)/assign-as-homework",
        Component: MyGamesTemplateAssignAsHomeworkPage,
        authType: "Teachers",
    },
    {
        path: "/my-games/my-prompts/:id([0-9]+)",
        Component: MyGamesTemplatePage,
        authType: "Teachers",
    },
    {
        path: "/my-games/past/player/:code([A-Za-z0-9]{6})",
        Component: MyGamesPastPlayerGamePage,
        authType: "Public",
    },
    {
        path: "/my-games/spectate/:code([A-Za-z0-9]{6})",
        Component: MyGamesSpectatePage,
        authType: "Public",
    },
    {
        path: "/my-games/projector/:code([A-Za-z0-9]{6})",
        Component: MyGamesProjectorPage,
        authType: "Public",
    },
    {
        path: "/my-games/host/:code([A-Za-z0-9]{6})/settings",
        Component: MyGamesHostGameSettingsPage,
        authType: "AuthRequired",
    },
    {
        path: "/my-games/enter/:code([A-Za-z0-9]{6})",
        Component: MyGamesEnterGamePage,
        authType: "Public",
    },
    {
        path: "/my-games/polish/:code([A-Za-z0-9]{6})",
        Component: MyGamesPolishGamePage,
        authType: "Public",
    },
    {
        path: "/classes/:id([0-9]+)/manage",
        Component: ClassesClassManagePage,
        authType: "Teachers",
    },
    {
        path: "/hall-of-fame/game/:code([A-Za-z0-9]{6})",
        Component: HallOfFameGamePage,
        authType: "Public",
    },
    {
        path: "/discover/category/:id([0-9]+)/:name",
        Component: DiscoverCategoryPage,
        authType: "Public",
    },
    {
        path: "/discover/prompt/:id([0-9]+)/:name",
        Component: DiscoverPromptPage,
        authType: "Public",
    },
    {
        path: "/discover/publisher/:name",
        Component: DiscoverPublisherPage,
        authType: "Public",
    },
    {
        path: RouteList.DiscoverSearch,
        Component: DiscoverSearchPage,
        authType: "Public",
    },
    {
        path: RouteList.Discover,
        Component: DiscoverPage,
        authType: "Public",
    },
    {
        path: RouteList.HallOfFameSearch,
        Component: HallOfFameSearchPage,
        authType: "Public",
    },
    {
        path: RouteList.HallOfFame,
        Component: HallOfFamePage,
        authType: "Public",
    },
    {
        path: "/discover/collection/:id([0-9]+)/:name",
        Component: DiscoverSetPage,
        authType: "Public",
    },
    {
        path: "/classes/:id([0-9]+)",
        Component: ClassesClassPage,
        authType: "AuthRequired",
    },
    {
        path: "/classes/:classId([0-9]+)/add-students",
        Component: ClassesAddStudentsPage,
        authType: "Teachers",
    },
    {
        path: "/classes/:classId([0-9]+)/games",
        Component: ClassesClassGamesPage,
        authType: "AuthRequired",
    },
    {
        path: "/classes/:classId([0-9]+)/showcase",
        Component: ClassesClassShowcasePage,
        authType: "AuthRequired",
    },
    {
        path: "/classes/:classId([0-9]+)/:studentId([0-9]+)",
        Component: ClassesClassStudentPage,
        authType: "AuthRequired",
    },
    {
        path: "/classes/:classId([0-9]+)/:studentId([0-9]+)/replies",
        Component: ClassesClassStudentRepliesPage,
        authType: "AuthRequired",
    },
    {
        path: RouteList.Classes,
        Component: ClassesPage,
        authType: "AuthRequired",
    },
    {
        path: RouteList.ProfileReferralCode,
        Component: ProfileReferAFriendPage,
        authType: "Teachers",
    },
    {
        path: RouteList.ProfileDefaults,
        Component: ProfileDefaultsPage,
        authType: "Teachers",
    },
    {
        path: RouteList.Resources,
        Component: ResourcesPage,
        authType: "Public",
    },
    {
        path: RouteList.Faqs,
        Component: FaqsPage,
        authType: "Public",
    },
    {
        path: RouteList.Videos,
        Component: VideosPage,
        authType: "Public",
    },
    {
        path: RouteList.RequestInvoice,
        Component: RequestInvoicePage,
        authType: "NotStudents",
    },
    {
        path: RouteList.ReferAFriend,
        Component: ReferAFriendPage,
        authType: "NotStudents",
    },
    {
        path: RouteList.ProfileSubscriptionPurchaseSuccess,
        Component: SubscriptionsPurchaseSuccessPage,
        authType: "Teachers",
    },
    {
        path: RouteList.ProfileSubscription,
        Component: ProfileSubscriptionPage,
        authType: "Teachers",
    },
    {
        path: RouteList.ProfileWatchList,
        Component: ProfileWatchListPage,
        authType: "Teachers",
    },
    {
        path: RouteList.Contact,
        Component: ContactPage,
        authType: "Public",
    },
    {
        path: RouteList.Newsletter,
        Component: NewsletterPage,
        authType: "Public",
    },
    {
        path: RouteList.Home,
        Component: HomePage,
        authType: "Public",
    },
    {
        path: RouteList.ClassesCreate,
        Component: ClassesCreatePage,
        authType: "Teachers",
    },
    {
        path: RouteList.ProfileJoinSchool,
        Component: ProfileJoinSchoolPage,
        authType: "Teachers",
    },
    {
        path: RouteList.ProfileSchool,
        Component: ProfileMySchoolPage,
        authType: "Teachers",
    },
    {
        path: RouteList.ProfileLeaveSchool,
        Component: ProfileLeaveSchoolPage,
        authType: "Teachers",
    },
    {
        path: RouteList.Pricing,
        Component: PricingPage,
        authType: "NotStudents",
    },
    {
        path: RouteList.Random,
        Component: RandomPage,
        authType: "Public",
    },
    {
        path: RouteList.Terms,
        Component: TermsPage,
        authType: "Public",
    },
    {
        path: RouteList.Privacy,
        Component: PrivacyPage,
        authType: "Public",
    },
    {
        path: RouteList.Events,
        Component: EventsPage,
        authType: "Public",
    },
    {
        path: RouteList.Accessibility,
        Component: AccessibilityPage,
        authType: "Public",
    },
    {
        path: RouteList.AccountExists,
        Component: AccountExistsPage,
        authType: "AuthRequired",
    },
    {
        path: RouteList.CompetitionTerms,
        Component: CompetitionTermsPage,
        authType: "Public",
    },
    {
        path: RouteList.StudentWritingResources,
        Component: StudentWritingResourcesPage,
        authType: "Public",
    },
    {
        path: RouteList.Team,
        Component: TeamPage,
        authType: "Public",
    },
    {
        path: RouteList.Friends,
        Component: FriendsPage,
        authType: "Public",
    },
    {
        path: RouteList.Login,
        Component: LoginPage,
        authType: "AuthBlocked",
    },
    {
        path: RouteList.Signup,
        Component: RegisterPage,
        authType: "AuthBlocked",
    },
    {
        path: RouteList.SignupCompleteTeacher,
        Component: SignupCompleteTeacherPage,
        authType: "AuthRequired",
    },
    {
        path: RouteList.SignupCompleteStudent,
        Component: SignupCompleteStudentPage,
        authType: "AuthRequired",
    },
    {
        path: RouteList.ProfileChangePassword,
        Component: ProfileChangePasswordPage,
        authType: "NotStudents",
    },
    {
        path: RouteList.ProfileDetails,
        Component: ProfileDetailsPage,
        authType: "AuthRequired",
    },
    {
        path: RouteList.ProfileMyAvatars,
        Component: ProfileMyAvatarsPage,
        authType: "AuthRequired",
    },
    {
        path: RouteList.ProfilePublish,
        Component: ProfilePublishPage,
        authType: "Teachers",
    },
    {
        path: RouteList.ProfilePublishCreateSet,
        Component: ProfilePublishCreateSetPage,
        authType: "Teachers",
    },
    {
        path: RouteList.ProfilePublishRegister,
        Component: ProfilePublishRegisterPage,
        authType: "Teachers",
    },
    {
        path: RouteList.ProfilePublishUpdateProfile,
        Component: ProfilePublishUpdateProfilePage,
        authType: "Teachers",
    },
    {
        path: "/profile/publish/update-collection/:id([0-9]+)",
        Component: ProfilePublishUpdateSetPage,
        authType: "Teachers",
    },
    {
        path: "/profile/publish/collection/:id([0-9]+)/create-prompt",
        Component: ProfilePublishSetCreatePromptPage,
        authType: "Teachers",
    },
    {
        path: "/profile/publish/collection/:setId([0-9]+)/update-prompt/:promptId([0-9]+)",
        Component: ProfilePublishSetUpdatePromptPage,
        authType: "Teachers",
    },
    {
        path: "/profile/publish/collection/:id([0-9]+)/import",
        Component: ProfilePublishSetImportPage,
        authType: "Teachers",
    },
    {
        path: "/profile/publish/collection/:id([0-9]+)",
        Component: ProfilePublishSetPage,
        authType: "Teachers",
    },
    {
        path: RouteList.ClassesJoin,
        Component: ClassesJoinPage,
        authType: "AuthRequired",
    },
    {
        path: RouteList.EduTech,
        Component: EduTechPage,
        authType: "Public",
    },
    {
        path: RouteList.Yearbook,
        Component: YearbookPage,
        authType: "Public",
    },
    {
        path: "/discover/prompts/:setName/:promptName",
        Component: DiscoverFindPromptPage,
        authType: "Public",
    },
    {
        path: "/discover/prompts/:setName",
        Component: DiscoverFindSetPage,
        authType: "Public",
    },
];

export const RouteLink = {
    approveUser: (hash: string) => `/approve-user/${hash}`,
    classesAddStudents: (id: number | string) => `/classes/${id}/add-students`,
    classesClass: (classId: number | string) => `/classes/${classId}`,
    classesClassGames: (classId: number) => `/classes/${classId}/games`,
    classesClassManage: (id: number | string) => `/classes/${id}/manage`,
    classesClassShowcase: (classId: number) => `/classes/${classId}/showcase`,
    classesClassStudent: (classId: number, studentId: number) => `/classes/${classId}/${studentId}`,
    classesClassStudentReplies: (classId: number, studentId: number) => `/classes/${classId}/${studentId}/replies`,
    discoverCategory: (id: number, name: string) => `/discover/category/${id}/${toSafeUrlName(name)}`,
    discoverPrompt: (id: number, name: string) => `/discover/prompt/${id}/${toSafeUrlName(name)}`,
    discoverSet: (id: number, name: string) => `/discover/collection/${id}/${toSafeUrlName(name)}`,
    discoverPublisher: (name: string) => `/discover/publisher/${toSafeUrlName(name)}`,
    hallOfFameGame: (code: string) => `/hall-of-fame/game/${code}`,
    hallOfFameSearch: (search: HallOfFameSearchFormData) => {
        const params = new URLSearchParams();

        if (search.query) {
            params.set(HallOfFameSearchParams.query, encodeURIComponent(search.query));
        }

        if (search.category) {
            params.set(HallOfFameSearchParams.category, search.category.toString());
        }

        if (search.language) {
            params.set(HallOfFameSearchParams.language, search.language);
        }

        if (search.country) {
            params.set(HallOfFameSearchParams.country, search.country);
        }

        if (search.region) {
            params.set(HallOfFameSearchParams.region, search.region);
        }

        if (search.schoolLevel) {
            params.set(HallOfFameSearchParams.schoolLevel, search.schoolLevel.toString());
        }

        if (params.toString()) {
            return `${RouteList.HallOfFameSearch}?${params.toString()}`;
        } else {
            return RouteList.HallOfFameSearch;
        }
    },
    myGamesEnter: (c: string) => `/my-games/enter/${c}`,
    myGamesPolishGame: (c: string) => `/my-games/polish/${c}`,
    myGamesGame: (c: string) => `/my-games/game/${c}`,
    myGamesHomeworkClass: (id: number) => `/my-games/homework?c=${id}`,
    myGamesHostClass: (id: number) => `${RouteList.MyGamesActiveHost}?c=${id}`,
    myGamesHostGame: (c: string) => `/my-games/host/${c}`,
    myGamesHostGameSettings: (c: string) => `/my-games/host/${c}/settings`,
    myGamesActiveHostMulti: (codes: Array<string>) => `${RouteList.MyGamesActiveHostMulti}?codes=${codes.join(",")}`,
    myGamesPastHostClass: (id: number) => `${RouteList.MyGamesPastHost}?c=${id}`,
    myGamesPastHostGame: (c: string) => `/my-games/past/host/${c}`,
    myGamesPastPlayerGame: (c: string) => `/my-games/past/player/${c}`,
    myGamesPrompt: (id: number | string) => `/my-games/my-prompts/${id}`,
    myGamesPromptAssignAsHomework: (id: number) => `/my-games/my-prompts/${id}/assign-as-homework`,
    myGamesPromptAssignToClass: (id: number) => `/my-games/my-prompts/${id}/assign-to-class`,
    myGamesPromptEdit: (id: number) => `/my-games/my-prompts/${id}/edit`,
    myGamesSpectate: (c: string) => `/my-games/spectate/${c}`,
    myGamesProjector: (c: string) => `/my-games/projector/${c}`,
    profilePublishSet: (id: number | string) => `/profile/publish/collection/${id}`,
    profilePublishSetCreatePrompt: (id: number | string) => `/profile/publish/collection/${id}/create-prompt`,
    profilePublishSetImportPrompt: (id: number | string) => `/profile/publish/collection/${id}/import`,
    profilePublishSetUpdatePrompt: (promptId: number | string, setId: number | string) =>
        `/profile/publish/collection/${setId}/update-prompt/${promptId}`,
    profilePublishUpdateSet: (id: number | string) => `/profile/publish/update-collection/${id}`,
    recoverPassword: (hash: string) => `/recover-password/${hash}`,
    signupCompleteStudent: (c: string) => `/signup-complete-student?c=${c}`,
};
